import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BellRing, CalendarClock, ArrowRight, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { leadsAPI } from '@/lib/api';

const HOUR_MS = 60 * 60 * 1000;

function fmt(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

// Phase 27.28 Fase A — Upcoming follow-ups tile.
// Compact card for the agent/agency dashboard showing the next 5
// pending follow-ups so agents don't miss any lead. Overdue rows are
// tinted red, due-soon (< 1h) rows are amber.
export default function UpcomingFollowupsWidget({ limit = 5 }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await leadsAPI.upcomingFollowUps(limit);
      setRows(res.data.follow_ups || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [limit]);

  useEffect(() => { load(); }, [load]);

  return (
    <Card
      className="mb-8 animate-slideIn stagger-1 border-l-4 border-l-amber-500"
      data-testid="upcoming-followups-widget"
    >
      <CardHeader className="flex flex-row items-center justify-between py-3">
        <CardTitle className="text-base font-heading flex items-center gap-2">
          <BellRing size={16} className="text-amber-600" />
          Upcoming Follow-ups
        </CardTitle>
        <Link to="/leads">
          <Button variant="ghost" size="sm" className="text-xs">
            All leads <ArrowRight size={12} className="ml-1" />
          </Button>
        </Link>
      </CardHeader>
      <CardContent className="pt-0">
        {loading ? (
          <div className="p-4 text-slate-500 flex items-center gap-2 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="p-4 text-slate-500 text-sm" data-testid="followups-widget-empty">
            No follow-ups scheduled. Schedule one from any lead detail page.
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {rows.map((r) => {
              const scheduledMs = new Date(r.scheduled_at).getTime();
              const now = Date.now();
              const isDueSoon = scheduledMs - now <= HOUR_MS && scheduledMs > now;
              const isOverdue = scheduledMs <= now;
              const leadMissing = r.lead_exists === false;
              const rowInner = (
                <>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      {leadMissing ? (
                        <span className="font-medium text-slate-500 italic">
                          {r.lead_name || 'Related record unavailable'}
                        </span>
                      ) : (
                        <span className="font-medium text-slate-900">{r.lead_name}</span>
                      )}
                      {leadMissing && (
                        <Badge className="bg-slate-100 text-slate-500 border-slate-300 border text-[10px] font-normal">
                          Related record unavailable
                        </Badge>
                      )}
                      {isOverdue && <Badge className="bg-rose-100 text-rose-700 border-rose-300 border text-[10px] font-normal">OVERDUE</Badge>}
                      {isDueSoon && <Badge className="bg-amber-100 text-amber-800 border-amber-300 border text-[10px] font-normal">DUE SOON</Badge>}
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1">
                      <CalendarClock size={10} /> {fmt(r.scheduled_at)}
                      {r.lead_phone && ` · ${r.lead_phone}`}
                    </p>
                    {r.note && <p className="text-xs text-slate-600 italic mt-0.5 truncate">{r.note}</p>}
                  </div>
                  <ArrowRight size={12} className={`shrink-0 ${leadMissing ? 'text-slate-200' : 'text-slate-300'}`} />
                </>
              );
              if (leadMissing) {
                return (
                  <div
                    key={r.follow_up_id}
                    className="flex items-center justify-between py-2.5 -mx-3 px-3 rounded opacity-75 cursor-default"
                    data-testid={`followup-widget-row-${r.follow_up_id}`}
                    title="The related lead is no longer available."
                  >
                    {rowInner}
                  </div>
                );
              }
              return (
                <Link
                  key={r.follow_up_id}
                  to={`/leads/${r.lead_id}`}
                  className="flex items-center justify-between py-2.5 hover:bg-slate-50 -mx-3 px-3 rounded"
                  data-testid={`followup-widget-row-${r.follow_up_id}`}
                >
                  {rowInner}
                </Link>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
